import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
    Box, Typography, Button, TextField, Table, TableBody, MenuItem, 
    TableCell, TableContainer, TableHead, TableRow, Paper, Stack, 
    Grid, Tabs, Tab, RadioGroup, FormControlLabel, Radio, Checkbox, 
    Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, 
    IconButton, Divider 
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'; 
import DeleteIcon from '@mui/icons-material/Delete';
import AttachmentIcon from '@mui/icons-material/Attachment';
import axios from 'axios';
import * as XLSX from 'xlsx';
import DocumentPicker from './DocumentPicker';

// Helper for form rows in dialogs
const FormRow = ({ label, children }) => (
    <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
        <Grid item xs={12} md={4}>
            <Typography variant="body2">{label}</Typography>
        </Grid>
        <Grid item xs={12} md={8}>
            {children}
        </Grid>
    </Grid>
);

const CurrencyAdornment = () => (
    <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary' }}>R</Typography>
);

// ==========================================
// 1. CREDITOR MANAGEMENT SCREEN (MAIN LIST)
// ==========================================
const CreditorManagement = ({ onOpenCreditor, creditors, onAddCreditor, onOpenDirectory, onDeleteCreditor, onPrint, selectedCreditorId, onSelectCreditor, onShowAllClaims, onExport }) => {
    const [findCreditor, setFindCreditor] = useState('');
    const [filterText, setFilterText] = useState('');

    const tableHeaders = [
        "Name of Creditor", "Phone Number", "Address", 
        "No. of Claims", "Total Value of Claims", "Contact Person", "Email address"
    ];

    // Search and filter logic
    const filteredCreditors = useMemo(() => {
        return creditors.filter(creditor => {
            const matchesFind = findCreditor === '' || 
                (creditor.name && creditor.name.toLowerCase().includes(findCreditor.toLowerCase()));
            
            const matchesFilter = filterText === '' || 
                Object.values(creditor).some(val => 
                    val && val.toString().toLowerCase().includes(filterText.toLowerCase())
                );

            return matchesFind && matchesFilter;
        });
    }, [creditors, findCreditor, filterText]);

    const handleChangeClick = () => {
        if (selectedCreditorId) {
            const creditorToOpen = creditors.find(c => String(c.id) === String(selectedCreditorId));
            if (creditorToOpen) {
                onOpenCreditor(creditorToOpen);
            }
        }
    };

    return (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="caption" sx={{ mb: 2 }}>
                Creditors are only added ONCE. Then go to the "List of ALL Claims for this Creditor" tab and add all the claims for this creditor there. Click on Insert below to add the creditor.
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ flexGrow: 1, mb: 2 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 40 }}>&nbsp;</TableCell>
                            {tableHeaders.map((header, index) => (
                                <TableCell key={index} sx={{ fontWeight: 'normal', whiteSpace: 'nowrap' }}>
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCreditors.map((creditor) => (
                            <TableRow 
                                key={creditor.id} 
                                hover 
                                onClick={() => onSelectCreditor(creditor.id)}
                                onDoubleClick={() => onOpenCreditor(creditor)} 
                                sx={{ cursor: 'pointer', bgcolor: String(selectedCreditorId) === String(creditor.id) ? 'action.selected' : 'transparent' }}>
                                <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>»</TableCell>
                                <TableCell>{creditor.name || '-'}</TableCell>
                                <TableCell>{creditor.phone || '-'}</TableCell>
                                <TableCell>{creditor.cell || '-'}</TableCell>
                                <TableCell>{(creditor.claims || []).length}</TableCell>
                                <TableCell align="right">
                                    R {(creditor.claims || []).reduce((sum, claim) => sum + (parseFloat(claim.provisionalValue) || 0), 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell>
                                    {creditor.contactPerson || '-'}
                                </TableCell>
                                <TableCell>{creditor.email || '-'}</TableCell> 
                            </TableRow>
                        ))}
                        {filteredCreditors.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        No creditors found for this matter. Click "Insert" below to add one.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">Find creditor:</Typography>
                            <TextField size="small" value={findCreditor} onChange={(e) => setFindCreditor(e.target.value)} />
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                            <Typography variant="body2">Filter:</Typography>
                            <TextField size="small" value={filterText} onChange={(e) => setFilterText(e.target.value)} />
                            <Button variant="outlined" size="small" onClick={() => { setFindCreditor(''); setFilterText(''); }}>Clear</Button>
                        </Stack>
                    </Grid>
                </Grid>

                <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
                    <Button variant="outlined" size="small" onClick={onAddCreditor} startIcon={<AddCircleOutlineIcon />}>Insert</Button>
                    <Button variant="outlined" size="small" disabled={!selectedCreditorId} onClick={handleChangeClick}>Change</Button>
                    <Button variant="outlined" size="small" disabled={!selectedCreditorId} onClick={() => onDeleteCreditor(selectedCreditorId)}>Delete</Button> 
                    <Button variant="outlined" size="small" onClick={onShowAllClaims}>All Claims</Button>
                    <Button variant="outlined" size="small" onClick={onOpenDirectory}>Creditor Directory</Button>
                    <Button variant="outlined" size="small" onClick={onExport}>Export</Button>
                    <Button variant="outlined" size="small" onClick={onPrint}>Print</Button>
                </Stack>
            </Paper>
        </Box>
    );
};

// ==========================================
// 2. CREDITOR DETAILS TABS
// ==========================================
const CreditorDetailsTabs = ({ creditor, onBack, onSave, onClaimOpen, onClaimDelete, creditorDirectory, onSelectFromDirectory }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [selectedClaimId, setSelectedClaimId] = useState(null);
    const [creditorForm, setCreditorForm] = useState({ 
        creditorType: 'Juristic',
        ...creditor, 
        grossProvisionalOverride: creditor?.grossProvisionalOverride || '' 
    });
    const [isGrossValueFocused, setIsGrossValueFocused] = useState(false); 

    // Prevent resetting state on every render using a ref check
    const currentCreditorIdRef = useRef(creditor?.id);
    useEffect(() => {
        if (creditor?.id !== currentCreditorIdRef.current) {
            currentCreditorIdRef.current = creditor?.id;
            setCreditorForm({ 
                creditorType: 'Juristic',
                ...creditor, 
                grossProvisionalOverride: creditor?.grossProvisionalOverride || '' 
            });
        }
    }, [creditor]);

    const totalClaimValue = useMemo(() => {
        return (creditorForm.claims || []).reduce((total, claim) => {
            return total + (parseFloat(claim.provisionalValue) || 0);
        }, 0);
    }, [creditorForm.claims]);

    const getGrossDisplayValue = () => {
        const overrideValue = creditorForm.grossProvisionalOverride;
        if (isGrossValueFocused) {
            return overrideValue;
        }
        const valueToFormat = (overrideValue !== '' && overrideValue != null) ? overrideValue : totalClaimValue;
        const num = parseFloat(valueToFormat);
        if (isNaN(num)) return 'R 0.00';
        return num.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' });
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCreditorForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleClaimSelection = (claimId) => {
        setSelectedClaimId(prev => (prev === claimId ? null : claimId));
    };

    const registrationLabel = (creditorForm.creditorType === 'Male' || creditorForm.creditorType === 'Female')
        ? "Identity Number"
        : "Registration No.";

    return (
        <Box sx={{ p: 2 }}>
            <Button onClick={onBack} sx={{ mb: 2 }}>&larr; Back to Creditor List</Button>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                <Tab label="1. Creditor's Details" />
                <Tab label="2. Other Details" />
                <Tab label="3. List of ALL Claims for this Creditor" />
            </Tabs>

            {/* Tab 1: Creditor Details */}
            {activeTab === 0 && (
                <Paper variant="outlined" sx={{ p: 3 }}>
                    <Autocomplete
                        options={creditorDirectory}
                        getOptionLabel={(option) => `${option.name} (${option.email || 'No Email'})`}
                        onChange={(e, value) => {
                            if (value) {
                                setCreditorForm(prev => ({
                                    ...prev,
                                    ...value,
                                    creditorType: value.creditorType || 'Juristic'
                                }));
                                onSelectFromDirectory(value);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Load from Directory..." size="small" sx={{ mb: 3 }} />
                        )}
                        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                    />
                    <Divider sx={{ mb: 3 }} />

                    <FormRow label="Indicate the type of Creditor">
                        <RadioGroup row name="creditorType" value={creditorForm.creditorType || 'Juristic'} onChange={handleFormChange}>
                            <FormControlLabel value="Male" control={<Radio size="small" />} label="Male" />
                            <FormControlLabel value="Female" control={<Radio size="small" />} label="Female" />
                            <FormControlLabel value="Juristic" control={<Radio size="small" />} label="Juristic" />
                            <FormControlLabel value="Other" control={<Radio size="small" />} label="Other" />
                        </RadioGroup>
                    </FormRow>
                    {(creditorForm.creditorType === 'Juristic' || creditorForm.creditorType === 'Other') && (
                        <FormRow label="Names of Authorised Person/s"><TextField fullWidth size="small" name="authorizedPersons" value={creditorForm.authorizedPersons || ''} onChange={handleFormChange} /></FormRow>
                    )}
                    <FormRow label="Registered Name of Creditor"><TextField fullWidth size="small" name="name" value={creditorForm.name || ''} onChange={handleFormChange} /></FormRow>
                    <FormRow label={registrationLabel}><TextField sx={{ width: '50%' }} size="small" name="registrationNo" value={creditorForm.registrationNo || ''} onChange={handleFormChange} /></FormRow>
                    <FormRow label="Creditor's address"><TextField fullWidth multiline rows={4} size="small" name="address" value={creditorForm.address || ''} onChange={handleFormChange} /></FormRow>
                    <FormRow label="Creditor's phone number"><TextField fullWidth size="small" name="phone" value={creditorForm.phone || ''} onChange={handleFormChange} /></FormRow>
                    <FormRow label="Creditor cell number"><TextField sx={{ width: '50%' }} size="small" name="cell" value={creditorForm.cell || ''} onChange={handleFormChange} /></FormRow>
                    <FormRow label="Creditor's e-mail address"><TextField sx={{ width: '75%' }} size="small" name="email" value={creditorForm.email || ''} onChange={handleFormChange} /></FormRow>
                    {(creditorForm.creditorType === 'Juristic' || creditorForm.creditorType === 'Other') && (
                        <FormRow label="Contact person">
                            <Stack direction="row" spacing={2} alignItems="center">
                                <TextField fullWidth size="small" name="contactPerson" value={creditorForm.contactPerson || ''} onChange={handleFormChange} />
                                {creditorForm.creditorType === 'Other' && (
                                    <>
                                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Reference No.</Typography>
                                        <TextField sx={{ flexGrow: 1 }} size="small" name="reference" value={creditorForm.reference || ''} onChange={handleFormChange} />
                                    </>
                                )}
                            </Stack>
                        </FormRow>
                    )}
                    <FormRow label="Gross provisional value of ALL claims for this creditor">
                        <TextField
                            name="grossProvisionalOverride"
                            size="small"
                            value={getGrossDisplayValue()}
                            onChange={(e) => handleFormChange({ target: { name: 'grossProvisionalOverride', value: e.target.value.replace(/[^0-9.]/g, '') } })}
                            onFocus={() => setIsGrossValueFocused(true)}
                            onBlur={() => setIsGrossValueFocused(false)}
                            placeholder={`Auto: ${totalClaimValue.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' })}`}
                            sx={{ width: '150px' }}
                        />
                    </FormRow>
                    <FormControlLabel control={<Checkbox name="saveToDirectory" checked={creditorForm.saveToDirectory || false} onChange={handleFormChange} />} label="Save/Update this creditor in the central directory" />

                    <Box sx={{ mt: 2 }}>
                        <Button onClick={() => onSave(creditorForm)} variant="contained">Save Creditor</Button>
                    </Box>
                </Paper>
            )}

            {/* Tab 2: Other Details */}
            {activeTab === 1 && (
                <Paper variant="outlined" sx={{ p: 3 }}>
                    <FormRow label="Name of Creditor's firm of Attorneys"><TextField fullWidth size="small" name="attorneyFirm" value={creditorForm.attorneyFirm || ''} onChange={handleFormChange} /></FormRow>
                    <FormRow label="Address of Creditor's Attorneys"><TextField fullWidth multiline rows={4} size="small" name="attorneyAddress" value={creditorForm.attorneyAddress || ''} onChange={handleFormChange} /></FormRow>
                    <FormRow label="Phone number of Creditor's Attorneys"><TextField sx={{ width: '50%' }} size="small" name="attorneyPhone" value={creditorForm.attorneyPhone || ''} onChange={handleFormChange} /></FormRow>
                    <FormRow label="E-Mail address of Creditor's Attorneys"><TextField sx={{ width: '75%' }} size="small" name="attorneyEmail" value={creditorForm.attorneyEmail || ''} onChange={handleFormChange} /></FormRow>
                    <FormRow label="Contact person at Attorney's Firm"><TextField fullWidth size="small" name="attorneyContact" value={creditorForm.attorneyContact || ''} onChange={handleFormChange} /></FormRow>
                    <Button onClick={() => onSave(creditorForm)} variant="contained" sx={{ mt: 2 }}>Save Creditor</Button>
                </Paper>
            )}

            {/* Tab 3: Claims List */}
            {activeTab === 2 && (
                <Paper variant="outlined" sx={{ p: 3 }}>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Doc</TableCell>
                                    <TableCell>Asset Linked to Claim</TableCell>
                                    <TableCell>Claim Number</TableCell>
                                    <TableCell>Reference Number</TableCell>
                                    <TableCell>Reason for Claim</TableCell>
                                    <TableCell>Claim Amount</TableCell>
                                    <TableCell>Proven Claim Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Proved Claim Number</TableCell>
                                    <TableCell>Type of Claim</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(creditorForm.claims || []).map(claim => (
                                    <TableRow 
                                        key={claim.id} 
                                        hover 
                                        onClick={() => handleClaimSelection(claim.id)}
                                        onDoubleClick={() => onClaimOpen(claim)} 
                                        sx={{ cursor: 'pointer', bgcolor: String(selectedClaimId) === String(claim.id) ? 'action.selected' : 'inherit' }}
                                    >
                                        <TableCell>
                                            {claim.claimDocumentUrl && (
                                                <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); onClaimOpen(claim, true); }}><AttachmentIcon fontSize="inherit" /></IconButton>
                                            )}
                                        </TableCell>
                                        <TableCell>{claim.linkedAssetName || '-'}</TableCell>
                                        <TableCell>{claim.claimNumber || '-'}</TableCell>
                                        <TableCell>{claim.accountNumber || '-'}</TableCell>
                                        <TableCell>{claim.reasonForClaim || '-'}</TableCell>
                                        <TableCell align="right">R {parseFloat(claim.provisionalValue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                        <TableCell align="right">{claim.isProved ? `R ${parseFloat(claim.provedValue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}</TableCell>
                                        <TableCell>{claim.status}</TableCell>
                                        <TableCell>{claim.provedNumber || '-'}</TableCell>
                                        <TableCell>
                                            {[
                                                claim.isSecured && 'Secured',
                                                claim.isPreferent && 'Preferent',
                                                claim.isConcurrent && 'Concurrent'
                                            ].filter(Boolean).join(', ') || 'N/A'}
                                        </TableCell>
                                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onClaimDelete(claim.id); }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={() => onClaimOpen(null)}>Insert</Button>
                        <Button variant="outlined" disabled={!selectedClaimId} onClick={() => onClaimOpen((creditorForm.claims || []).find(c => String(c.id) === String(selectedClaimId)))}>Change</Button> 
                        <Button variant="outlined" disabled={!selectedClaimId} onClick={() => onClaimDelete(selectedClaimId)}>Delete</Button>
                    </Stack>
                </Paper>
            )}
        </Box>
    );
};

// ==========================================
// 3. CLAIM DIALOG (POP-UP)
// ==========================================
const ClaimDialog = ({ open, onClose, onSave, claim, creditorName, assets = [], onOpenDocumentPicker, user }) => {
    const [claimForm, setClaimForm] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [focusedField, setFocusedField] = useState(null);

    const formatCurrency = (value) => {
        const num = parseFloat(value);
        if (isNaN(num)) return '0.00';
        return num.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const getDisplayValue = (fieldName) => {
        return focusedField === fieldName ? (claimForm[fieldName] ?? '') : formatCurrency(claimForm[fieldName]);
    };

    useEffect(() => {
        if (open) {
            const initialFormState = claim ? { ...claim } : {
                claimNumber: '',
                reasonForClaim: '',
                status: 'Not Received',
                accountNumber: '',
                provisionalValue: '0.00',
                accountHolder: '',
                dateOfClaim: '',
                dateReceived: '',
                dateAcknowledged: '',
                dateRejected: '',
                rejectionReason: '',
                isSecured: false,
                encumberedAssetAcNo: '0',
                encumberedAmount: '0.00',
                reliesExclusively: false,
                securityDescription: '',
                isPreferent: false,
                preferentValue: '0.00',
                isConcurrent: false,
                concurrentValue: '0.00',
                creditorName: creditorName,
                claimDocumentUrl: null,
                linkedAssetId: '', // 🎯 NEW: Linked Asset ID
                linkedAssetName: '', // 🎯 NEW: Linked Asset Name
            };
            setClaimForm(initialFormState);
            setActiveTab(0);
        }
    }, [open, claim, creditorName]);

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setClaimForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleCurrencyChange = (e) => {
        const { name, value } = e.target;
        const numericValue = value.replace(/[^0-9.]/g, '');
        setClaimForm(prev => ({ ...prev, [name]: numericValue }));
    };

    const handleSave = () => {
        onSave(claimForm);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{claim?.id ? 'Edit Claim' : 'Add New Claim'}</DialogTitle>
            {/* 🎯 NEW: Add a disabled prop to the DialogContent for guest users */}
            <DialogContent sx={{ p: 0 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="General Claim Info" />
                    <Tab label="L&D Claim Info" />
                </Tabs>

                <Box sx={{ p: 3, minHeight: 400 }}>
                    {activeTab === 0 && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormRow label="Claim no. (Before proved)"><TextField size="small" name="claimNumber" value={claimForm.claimNumber || ''} onChange={handleFormChange} sx={{ bgcolor: '#ffffcc', width: '120px' }} /></FormRow>
                                <FormRow label="Reference/Account Number:"><TextField size="small" name="accountNumber" value={claimForm.accountNumber || ''} onChange={handleFormChange} /></FormRow>
                                <FormRow label="Description of Account/Claim:"><TextField size="small" name="reasonForClaim" value={claimForm.reasonForClaim || ''} onChange={handleFormChange} /></FormRow>
                                <FormRow label="Provisional value of claim (R)">
                                    <TextField 
                                        size="small" 
                                        name="provisionalValue"
                                        value={getDisplayValue('provisionalValue')}
                                        onChange={handleCurrencyChange}
                                        InputProps={{ startAdornment: <CurrencyAdornment /> }}
                                        onFocus={() => setFocusedField('provisionalValue')}
                                        onBlur={() => setFocusedField(null)}
                                    />
                                </FormRow>
                                <FormRow label="In which name is account held?"><TextField size="small" name="accountHolder" value={claimForm.accountHolder || ''} onChange={handleFormChange} /></FormRow>
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="outlined"
                                    startIcon={claimForm.claimDocumentUrl ? <AttachmentIcon /> : <AddCircleOutlineIcon />}
                                    onClick={() => onOpenDocumentPicker(claimForm, (url) => setClaimForm(prev => ({ ...prev, claimDocumentUrl: url })))}
                                    disabled={user?.role === 'guest'}
                                    color={claimForm.claimDocumentUrl ? "success" : "primary"}
                                >
                                    {claimForm.claimDocumentUrl ? 'View/Change Claim Document' : 'Attach Claim Document'}
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Stack spacing={2}>
                                    <FormRow label="Date of claim"><TextField size="small" type="date" InputLabelProps={{ shrink: true }} name="dateOfClaim" value={claimForm.dateOfClaim ? claimForm.dateOfClaim.split('T')[0] : ''} onChange={handleFormChange} inputProps={{ max: '9999-12-31' }} disabled={user?.role === 'guest'} /></FormRow>
                                    <FormRow label="Date claim received"><TextField size="small" type="date" InputLabelProps={{ shrink: true }} name="dateReceived" value={claimForm.dateReceived ? claimForm.dateReceived.split('T')[0] : ''} onChange={handleFormChange} inputProps={{ max: '9999-12-31' }} disabled={user?.role === 'guest'} /></FormRow>
                                    <FormRow label="Date claim acknowledged"><TextField size="small" type="date" InputLabelProps={{ shrink: true }} name="dateAcknowledged" value={claimForm.dateAcknowledged ? claimForm.dateAcknowledged.split('T')[0] : ''} onChange={handleFormChange} inputProps={{ max: '9999-12-31' }} disabled={user?.role === 'guest'} /></FormRow>
                                    <FormRow label="Date on which rejected"><TextField size="small" type="date" InputLabelProps={{ shrink: true }} name="dateRejected" value={claimForm.dateRejected ? claimForm.dateRejected.split('T')[0] : ''} onChange={handleFormChange} inputProps={{ max: '9999-12-31' }} disabled={user?.role === 'guest'} /></FormRow>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormRow label="Why was claim rejected?">
                                    <TextField fullWidth multiline rows={5} size="small" name="rejectionReason" value={claimForm.rejectionReason || ''} onChange={handleFormChange} disabled={user?.role === 'guest'} />
                                </FormRow>
                            </Grid>
                            <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>
                            <Grid item xs={12}>
                                <Stack spacing={1}>
                                    <FormRow label="Was the claim proved?">
                                        <FormControlLabel control={<Checkbox name="isProved" checked={claimForm.isProved || false} onChange={handleFormChange} />} label="" />
                                    </FormRow>
                                    <FormRow label="Proved claim no. for distribution (R)">
                                        <TextField size="small" type="number" name="provedNumber" value={claimForm.provedNumber || ''} onChange={handleFormChange} sx={{ width: '120px' }} />
                                    </FormRow>
                                    <FormRow label="Date claim was proved">
                                        <TextField 
                                            size="small" type="date" InputLabelProps={{ shrink: true }} 
                                            name="dateProved"
                                            value={claimForm.dateProved ? claimForm.dateProved.split('T')[0] : ''}
                                            onChange={handleFormChange} sx={{ width: '150px' }} inputProps={{ max: '9999-12-31' }}
                                        />
                                    </FormRow>
                                    <FormRow label="Proved value for L&D purposes">
                                        <TextField 
                                            size="small" 
                                            name="provedValue" 
                                            value={getDisplayValue('provedValue')}
                                            onChange={handleCurrencyChange} 
                                            onFocus={() => setFocusedField('provedValue')}
                                            onBlur={() => setFocusedField(null)}
                                            InputProps={{ startAdornment: <CurrencyAdornment /> }} 
                                        />
                                    </FormRow>
                                </Stack>
                            </Grid>
                            {/* 🎯 NEW: Linked Asset Field */}
                            <Grid item xs={12}>
                                <FormRow label="Link to Asset:">
                                    <Autocomplete
                                        fullWidth
                                        options={assets}
                                        getOptionLabel={(option) => `${option.asset_description || option.streetAddress || option.defName || 'Unnamed Asset'} (ID: ${option.id})`}
                                        value={assets.find(asset => String(asset.id) === String(claimForm.linkedAssetId)) || null}
                                        onChange={(event, newValue) => {
                                            setClaimForm(prev => ({ ...prev, linkedAssetId: newValue ? newValue.id : '', linkedAssetName: newValue ? (newValue.asset_description || newValue.streetAddress || newValue.defName) : '' }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Select Linked Asset" variant="outlined" size="small" />
                                        )}
                                        disabled={user?.role === 'guest'}
                                    />
                                </FormRow>
                            </Grid>

                        </Grid>
                    )}

                    {activeTab === 1 && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <FormRow label="Claim Status">
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    name="status"
                                    value={claimForm.status || 'Not Received'}
                                    onChange={handleFormChange}
                                >
                                    {['Not Received', 'Obtained', 'Proven', 'Rejected', 'Withdrawn', 'Expunge'].map(opt => (<MenuItem key={opt} value={opt}>{opt}</MenuItem>))}
                                </TextField>
                            </FormRow>
                            <Stack spacing={2} disabled={user?.role === 'guest'}>
                                <FormRow label="Has this claim been secured/encumbered?">
                                    <FormControlLabel control={<Checkbox name="isSecured" checked={claimForm.isSecured || false} onChange={handleFormChange} />} label="" />
                                </FormRow>
                                <FormRow label="If so, what is the encumbered asset a/c no?">
                                    <Autocomplete
                                        options={assets}
                                        getOptionLabel={(option) => {
                                            const label = option.asset_description || option.tenantName || option.registrationNumber || option.defName || 'Unnamed Asset';
                                            return `${label} (Acct: ${option.id})`;
                                        }}
                                        value={assets.find(a => String(a.id) === String(claimForm.encumberedAssetAcNo)) || null}
                                        onChange={(e, newValue) => {
                                            handleFormChange({ target: { name: 'encumberedAssetAcNo', value: newValue ? newValue.id : '' } });
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} size="small" label="Select Asset" sx={{ bgcolor: '#ffffcc' }} />
                                        )}
                                        sx={{ width: '100%' }}
                                    />
                                </FormRow>
                                <FormRow label="If so, by which amount?">
                                    <TextField
                                        size="small"
                                        name="encumberedAmount"
                                        value={getDisplayValue('encumberedAmount')}
                                        onChange={handleCurrencyChange}
                                        onFocus={() => setFocusedField('encumberedAmount')}
                                        onBlur={() => setFocusedField(null)}
                                        sx={{ width: '150px' }}
                                        InputProps={{ startAdornment: <CurrencyAdornment /> }}
                                    />
                                </FormRow>
                                <FormRow label="Does the Creditor rely exclusively on his security?">
                                    <FormControlLabel control={<Checkbox name="reliesExclusively" checked={claimForm.reliesExclusively || false} onChange={handleFormChange} />} label="" />
                                </FormRow>
                                <FormRow label="Describe the encumberance/security:">
                                    <TextField fullWidth size="small" name="securityDescription" value={claimForm.securityDescription || ''} onChange={handleFormChange} placeholder="e.g. Mortgage Bond" />
                                </FormRow>

                                <Divider sx={{ my: 2 }} />

                                <FormRow label="Is this a preferent claim?">
                                    <FormControlLabel control={<Checkbox name="isPreferent" checked={claimForm.isPreferent || false} onChange={handleFormChange} />} label="" />
                                </FormRow>
                                <FormRow label="State preferent value as Act prescribes">
                                    <TextField
                                        size="small"
                                        name="preferentValue"
                                        value={getDisplayValue('preferentValue')}
                                        onChange={handleCurrencyChange}
                                        onFocus={() => setFocusedField('preferentValue')}
                                        onBlur={() => setFocusedField(null)}
                                        sx={{ width: '150px' }}
                                        InputProps={{ startAdornment: <CurrencyAdornment /> }}
                                    />
                                </FormRow>

                                <Divider sx={{ my: 2 }} />

                                <FormRow label="Is this a concurrent claim?">
                                    <FormControlLabel control={<Checkbox name="isConcurrent" checked={claimForm.isConcurrent || false} onChange={handleFormChange} />} label="" />
                                </FormRow>
                                <FormRow label="State concurrent value">
                                    <TextField
                                        size="small"
                                        name="concurrentValue"
                                        value={getDisplayValue('concurrentValue')}
                                        onChange={handleCurrencyChange}
                                        sx={{ width: '150px' }}
                                        InputProps={{ startAdornment: <CurrencyAdornment /> }}
                                        onFocus={() => setFocusedField('concurrentValue')}
                                        onBlur={() => setFocusedField(null)}
                                        disabled={user?.role === 'guest'}
                                    />
                                </FormRow>
                            </Stack>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions disabled={user?.role === 'guest'}>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save Claim</Button>
            </DialogActions>
        </Dialog>
    );
};

// ==========================================
// 4. MAIN COMPONENT
// ==========================================
export default function ClaimsTab({ formData, handleFieldChange, isEditingMode, user, matterId }) {
    const [screen, setScreen] = useState('management');
    const [selectedCreditorId, setSelectedCreditorId] = useState(null);
    const [selectedCreditor, setSelectedCreditor] = useState(null);
    const [selectedClaim, setSelectedClaim] = useState(null);
    const [claimDialogOpen, setClaimDialogOpen] = useState(false);
    const [creditorDirectory, setCreditorDirectory] = useState([]);
    const [documentPickerOpen, setDocumentPickerOpen] = useState(false);
    const [directoryOpen, setDirectoryOpen] = useState(false);
    const [allClaimsModalOpen, setAllClaimsModalOpen] = useState(false);
    const [claimDocCallback, setClaimDocCallback] = useState(null);

    const allClaims = useMemo(() => formData?.claimsList || [], [formData?.claimsList]);

    // 🎯 Fetched independently (not from the `formData` prop, which is only loaded once
    // on page load and never refreshed) so newly added/edited assets show up here without
    // requiring a full page reload.
    const [allAssets, setAllAssets] = useState([]);
    useEffect(() => {
        const fetchAssets = async () => {
            if (!matterId) return;
            try {
                const res = await axios.get('/api/immoveables', { params: { legalMatterId: matterId } });
                setAllAssets(Array.isArray(res.data) ? res.data : []);
            } catch (e) {
                console.error("Failed to fetch assets for linking", e);
            }
        };
        fetchAssets();
    }, [matterId]);

    // 🎯 Defensive parsing handles arrays, JSON strings from SQL, or single values
const creditors = useMemo(() => {
    let rawCreditorIds = formData?.creditorIds || [];

    // Parse JSON string if DB returned a stringified array e.g. "[18]"
    if (typeof rawCreditorIds === 'string') {
        try {
            rawCreditorIds = JSON.parse(rawCreditorIds);
        } catch (e) {
            rawCreditorIds = [];
        }
    }
    if (!Array.isArray(rawCreditorIds)) {
        rawCreditorIds = [rawCreditorIds].filter(Boolean);
    }

    const claimCreditorIds = new Set(allClaims.map(c => String(c.creditorId)).filter(Boolean));
    const matterCreditorIds = new Set(rawCreditorIds.map(id => String(id)).filter(Boolean));
    
    const allMatterCreditorIds = new Set([...claimCreditorIds, ...matterCreditorIds]);

    if (allMatterCreditorIds.size === 0) return [];

    return Array.from(allMatterCreditorIds).map(idStr => {
        const dirCreditor = creditorDirectory.find(c => String(c.id) === idStr);
        const claimsForThisCreditor = allClaims.filter(claim => String(claim.creditorId) === idStr);
        const fallbackName = claimsForThisCreditor[0]?.creditorName || `Creditor #${idStr}`;

        if (dirCreditor) {
            return {
                ...dirCreditor,
                claims: claimsForThisCreditor
            };
        }

        return {
            id: isNaN(Number(idStr)) ? idStr : Number(idStr),
            name: fallbackName,
            phone: '-',
            cell: '-',
            address: '-',
            contactPerson: '-',
            email: '-',
            claims: claimsForThisCreditor
        };
    });
}, [allClaims, creditorDirectory, formData?.creditorIds]);

const saveClaimsToDB = async (payload) => {
    if (!matterId) return;
    try {
        const claimsList = payload?.claimsList !== undefined 
            ? payload.claimsList 
            : (Array.isArray(payload) ? payload : allClaims);
            
        let creditorIds = payload?.creditorIds !== undefined 
            ? payload.creditorIds 
            : (formData?.creditorIds || []);

        if (typeof creditorIds === 'string') {
            try { creditorIds = JSON.parse(creditorIds); } catch(e) { creditorIds = []; }
        }

        await axios.put(`/api/legal-matter/${matterId}`, { 
            claimsList, 
            creditorIds 
        });
    } catch (e) {
        console.error("Auto-save failed", e);
    }
};

    useEffect(() => {
        const fetchCreditorDirectory = async () => {
            try {
                const res = await axios.get('/api/creditors');
                setCreditorDirectory(Array.isArray(res.data) ? res.data : []);
            } catch (e) { 
                console.error("Failed to fetch creditor directory", e); 
            }
        };
        fetchCreditorDirectory();
    }, []);

    const handleOpenCreditor = (creditor) => {
        setSelectedCreditor(creditor);
        setScreen('tabs');
    };

    const handleOpenClaim = (claim) => {
        setSelectedClaim(claim);
        setClaimDialogOpen(true);
    };

    const handleOpenClaimDocumentPicker = (claimData, callback) => {
        setSelectedClaim(claimData);
        setClaimDocCallback(() => callback);
        setDocumentPickerOpen(true);
    };

    const handleSelectCreditor = (id) => {
        setSelectedCreditorId(prev => (String(prev) === String(id) ? null : id));
    };

    const handleBackToManagement = () => {
        setSelectedCreditor(null);
        setScreen('management');
    };

    const handleSaveCreditor = async (creditorData) => {
        const { claims, ...baseCreditorData } = creditorData;

        const creditorToSave = Object.entries(baseCreditorData).reduce((acc, [key, value]) => {
            acc[key] = value === '' ? null : value;
            return acc;
        }, {});

        creditorToSave.name = baseCreditorData.name || '';
        creditorToSave.creditorType = baseCreditorData.creditorType || 'Juristic';

        const isNewCreditorForMatter = typeof creditorToSave.id === 'string' && creditorToSave.id.startsWith('new_');

        try {
            let savedCreditorResponse;
            if (isNewCreditorForMatter) {
                savedCreditorResponse = await axios.post('/api/creditors', creditorToSave);
            } else {
                const idForUpdate = selectedCreditor?.id || creditorToSave.id;
                savedCreditorResponse = await axios.put(`/api/creditors/${idForUpdate}`, creditorToSave);
            }

            const savedCreditor = savedCreditorResponse.data;

            const updatedClaimsList = allClaims.map(claim => {
                const matchesCreditor = String(claim.creditorId) === String(selectedCreditor?.id) || 
                                        String(claim.creditorId) === String(savedCreditor.id);
                if (matchesCreditor) {
                    return { 
                        ...claim, 
                        creditorId: savedCreditor.id, 
                        creditorName: savedCreditor.name 
                    };
                }
                return claim;
            });

            if (isNewCreditorForMatter) {
                setCreditorDirectory(prev => [...prev, savedCreditor]);
            } else {
                setCreditorDirectory(prev => prev.map(c => String(c.id) === String(savedCreditor.id) ? savedCreditor : c));
            }

            const existingIds = (formData?.creditorIds || []).filter(id => !String(id).startsWith('new_'));
            const alreadyHasId = existingIds.some(id => String(id) === String(savedCreditor.id));
            const updatedCreditorIds = alreadyHasId ? existingIds : [...existingIds, savedCreditor.id];

            handleFieldChange({ target: { name: 'claimsList', value: updatedClaimsList, type: 'array' } });
            handleFieldChange({ target: { name: 'creditorIds', value: updatedCreditorIds, type: 'array' } });

            await saveClaimsToDB({
                claimsList: updatedClaimsList,
                creditorIds: updatedCreditorIds
            });

            setSelectedCreditor(savedCreditor);
            alert('Creditor saved successfully!');
            handleBackToManagement();

        } catch (error) {
            console.error("Error saving creditor:", error);
            alert(`Failed to save creditor. ${error.response?.data?.message || error.message}`);
        }
    };

    const handleAddCreditor = () => {
        setSelectedCreditor({
            id: `new_${Date.now()}`,
            name: '',
            claims: [],
            creditorType: 'Juristic',
            phone: '', email: '', address: '', contactPerson: '', reference: ''
        });
        setScreen('tabs');
    };

    const handleSaveClaim = (updatedClaim) => {
        let updatedList;
        if (updatedClaim.id) {
            updatedList = allClaims.map(c => String(c.id) === String(updatedClaim.id) ? updatedClaim : c);
        } else {
            const newClaim = {
                ...updatedClaim,
                id: -Math.floor(Math.random() * 1000000),
                creditorId: updatedClaim.creditorId || selectedCreditor?.id,
                creditorName: selectedCreditor?.name,
            };
            updatedList = [...allClaims, newClaim];
        }
        handleFieldChange({ target: { name: 'claimsList', value: updatedList, type: 'array' } });
        saveClaimsToDB({ claimsList: updatedList });
        alert("Claim saved!");
        handleBackToManagement();
    };

    const handleDeleteCreditor = (creditorId) => {
        if (!creditorId) return;
        const creditorToDelete = creditors.find(c => String(c.id) === String(creditorId));
        if (!creditorToDelete) return;

        if (window.confirm(`Are you sure you want to delete "${creditorToDelete.name}" and all associated claims?`)) {
            const updatedClaims = allClaims.filter(claim => String(claim.creditorId) !== String(creditorToDelete.id));
            const updatedCreditorIds = (formData?.creditorIds || []).filter(id => String(id) !== String(creditorToDelete.id));

            handleFieldChange({ target: { name: 'claimsList', value: updatedClaims, type: 'array' } });
            handleFieldChange({ target: { name: 'creditorIds', value: updatedCreditorIds, type: 'array' } });

            saveClaimsToDB({
                claimsList: updatedClaims,
                creditorIds: updatedCreditorIds
            });
            setSelectedCreditorId(null);
            alert('Creditor deleted.');
        }
    };

    const handleDeleteClaim = (claimId) => {
        if (!claimId) return;
        if (window.confirm('Are you sure you want to delete this claim?')) {
            const updatedClaims = allClaims.filter(c => String(c.id) !== String(claimId));
            handleFieldChange({ target: { name: 'claimsList', value: updatedClaims, type: 'array' } });
            saveClaimsToDB({ claimsList: updatedClaims });

            if (selectedCreditor) {
                setSelectedCreditor(prev => ({
                    ...prev,
                    claims: (prev.claims || []).filter(c => String(c.id) !== String(claimId))
                }));
            }
            alert('Claim deleted.');
        }
    };

    const handlePrint = () => {
        const totalClaimsValue = creditors.reduce((total, cred) => {
            return total + (cred.claims || []).reduce((sum, claim) => sum + (parseFloat(claim.provisionalValue) || 0), 0);
        }, 0);

        const printWindow = window.open('', '_blank');
        printWindow.document.write('<html><head><title>Creditors List</title><style>body{font-family:sans-serif;font-size:10pt;} table{width:100%;border-collapse:collapse;} th,td{border:1px solid #ccc;padding:4px;text-align:left;} th{background-color:#f0f0f0;}</style></head><body>');
        printWindow.document.write(`<h1>Creditors List for Matter: ${formData.matterName || 'N/A'}</h1>`);
        printWindow.document.write(`<p><strong>Total Creditors:</strong> ${creditors.length}</p>`);
        printWindow.document.write(`<p><strong>Total Value of Claims:</strong> R ${totalClaimsValue.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p><hr/>`);

        creditors.forEach(creditor => {
            printWindow.document.write(`<h3>${creditor.name}</h3>`);
            printWindow.document.write(`<p><strong>Phone:</strong> ${creditor.phone || 'N/A'} | <strong>Email:</strong> ${creditor.email || 'N/A'} | <strong>Address:</strong> ${creditor.address || 'N/A'}</p>`);
            printWindow.document.write('<table><thead><tr><th>Claim #</th><th>Description</th><th>Amount</th><th>Status</th><th>Proved?</th></tr></thead><tbody>');
            (creditor.claims || []).forEach(claim => {
                printWindow.document.write(`<tr><td>${claim.claimNumber || '-'}</td><td>${claim.reasonForClaim || '-'}</td><td>R ${parseFloat(claim.provisionalValue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td><td>${claim.status || '-'}</td><td>${claim.isProved ? 'Yes' : 'No'}</td></tr>`);
            });
            printWindow.document.write('</tbody></table><br/>');
        });

        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
    };

    const handleExport = () => {
        const totalClaimsValue = creditors.reduce((total, cred) => total + (cred.claims || []).reduce((sum, claim) => sum + (parseFloat(claim.provisionalValue) || 0), 0), 0);
    
        const summary = [
            { A: "Matter Name:", B: formData.matterName || 'N/A' },
            { A: "Total Creditors:", B: creditors.length },
            { A: "Total Claims Value:", B: totalClaimsValue },
            {}
        ];
    
        const mainData = [];
        const headers = ['Creditor Name', 'Contact Person', 'Email', 'Phone', 'Claim No', 'Claim Description', 'Claim Amount', 'Claim Status'];
        mainData.push(headers);
    
        creditors.forEach(creditor => {
            if (creditor.claims && creditor.claims.length > 0) {
                creditor.claims.forEach((claim, index) => {
                    mainData.push([
                        index === 0 ? creditor.name : '',
                        index === 0 ? creditor.contactPerson : '',
                        index === 0 ? creditor.email : '',
                        index === 0 ? creditor.phone : '',
                        claim.claimNumber || '-',
                        claim.reasonForClaim || '-',
                        parseFloat(claim.provisionalValue || 0),
                        claim.status || 'N/A'
                    ]);
                });
            } else {
                mainData.push([creditor.name, creditor.contactPerson, creditor.email, creditor.phone, 'N/A', 'No claims submitted', 0, 'N/A']);
            }
        });
    
        const summarySheet = XLSX.utils.json_to_sheet(summary, { skipHeader: true });
        const mainSheet = XLSX.utils.aoa_to_sheet(mainData, { origin: 'A5' });
        const worksheet = { ...summarySheet, ...mainSheet };
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Creditors");
        XLSX.writeFile(workbook, `Creditors_${formData.matterName || matterId}.xlsx`);
    };

    const handleSelectFromDirectory = (creditor) => {
        if (!creditor) return;
        setSelectedCreditor(prev => ({
            ...prev,
            ...creditor,
            id: creditor.id,
            claims: prev?.claims || [],
        }));
    };

    const handleDeleteFromDirectory = async (id) => {
        if (window.confirm("Are you sure you want to permanently delete this creditor from the directory?")) {
            try {
                await axios.delete(`/api/creditors/${id}`);
                setCreditorDirectory(prev => prev.filter(c => String(c.id) !== String(id)));
            } catch (e) { 
                console.error("Error deleting from directory", e); 
            }
        }
    };

    return (
        <Box sx={{ p: 2, height: 'calc(100vh - 200px)' }}>
            {screen === 'management' && (
                <CreditorManagement
                    creditors={creditors}
                    onOpenCreditor={handleOpenCreditor}
                    onSelectCreditor={handleSelectCreditor}
                    onAddCreditor={handleAddCreditor}
                    onOpenDirectory={() => setDirectoryOpen(true)}
                    onDeleteCreditor={handleDeleteCreditor}
                    onPrint={handlePrint}
                    onShowAllClaims={() => setAllClaimsModalOpen(true)}
                    onExport={handleExport}
                    selectedCreditorId={selectedCreditorId}
                />
            )}

            {screen === 'tabs' && selectedCreditor && (
                <CreditorDetailsTabs
                    creditor={selectedCreditor}
                    onBack={handleBackToManagement}
                    onSave={handleSaveCreditor}
                    onClaimOpen={handleOpenClaim}
                    onClaimDelete={handleDeleteClaim}
                    creditorDirectory={creditorDirectory}
                    onSelectFromDirectory={handleSelectFromDirectory}
                />
            )}

            {claimDialogOpen && (
                <ClaimDialog
                    open={claimDialogOpen}
                    onClose={() => setClaimDialogOpen(false)}
                    onSave={handleSaveClaim}
                    claim={selectedClaim}
                    creditorName={selectedCreditor?.name}
                    user={user} // Pass user to ClaimDialog for disabled state
                    assets={allAssets}
                    onOpenDocumentPicker={handleOpenClaimDocumentPicker}
                />
            )}

            <Dialog open={allClaimsModalOpen} onClose={() => setAllClaimsModalOpen(false)} fullWidth maxWidth="lg">
                <DialogTitle>All Claims for this Matter</DialogTitle>
                <DialogContent>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Creditor</TableCell>
                                    <TableCell>Claim No</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Linked Asset</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allClaims.map(claim => (
                                    <TableRow key={claim.id}>
                                        <TableCell>{claim.creditorName}</TableCell>
                                        <TableCell>{claim.claimNumber}</TableCell>
                                        <TableCell>{claim.reasonForClaim}</TableCell>
                                        <TableCell align="right">R {parseFloat(claim.provisionalValue || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                                        <TableCell>{claim.status}</TableCell>
                                        <TableCell>{claim.linkedAssetName || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions><Button onClick={() => setAllClaimsModalOpen(false)}>Close</Button></DialogActions>
            </Dialog>

            {documentPickerOpen && (
                <DocumentPicker
                    open={documentPickerOpen}
                    onClose={() => setDocumentPickerOpen(false)}
                    matterId={matterId}
                    user={user}
                    activeDocPath={selectedClaim?.claimDocumentUrl}
                    onDocumentSelect={(filepath) => {
                        if (claimDocCallback) {
                            claimDocCallback(filepath);
                        }
                        setDocumentPickerOpen(false);
                    }}
                    folder="Claims"
                />
            )}

            <Dialog open={directoryOpen} onClose={() => setDirectoryOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Creditor Directory</DialogTitle>
                <DialogContent>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Contact Person</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {creditorDirectory.map(creditor => (
                                    <TableRow key={creditor.id}>
                                        <TableCell>{creditor.name}</TableCell>
                                        <TableCell>{creditor.contactPerson}</TableCell>
                                        <TableCell>{creditor.email}</TableCell>
                                        <TableCell>{creditor.phone}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" color="error" onClick={() => handleDeleteFromDirectory(creditor.id)}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions><Button onClick={() => setDirectoryOpen(false)}>Close</Button></DialogActions>
            </Dialog>
        </Box>
    );
}